import { useEffect, useState } from 'react';
import { Clock, ChefHat, Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { bildSrc } from '@/lib/api';
import { Preis } from '@/components/ErnaehrungPreis';

/* Rezeptansicht (10.10.2026, Muster Natty Gains): Kalorienziel wählen — Mengen, Nährwerte je Zutat und Summe passt der
   Server an (dieselbe Rundung wie im Plan). Zubereitung als nummerierte Schritte, Gewürze „nach Belieben“. */

const MAHLZEIT = { fruehstueck: 'Frühstück', mittag: 'Mittagessen', snack: 'Snack', abend: 'Abendessen' };
const SCHWIERIGKEIT = { einfach: 'Einfach', mittel: 'Mittel', aufwendig: 'Aufwendig' };
const BUDGET = { preiswert: 'Preiswert', mittel: 'Mittel', premium: 'Premium' };
const VORGABEN = [300, 400, 500, 600, 800];

const zahl = (v, nachkomma = 0) => Number(v || 0).toLocaleString('de-DE', { maximumFractionDigits: nachkomma });

export function NaehrChip({ n }) {
  return (
    <span className="inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[11px] tabular-nums text-foreground/80">
      {zahl(n.kcal)} kcal | {zahl(n.eiweiss)} E | {zahl(n.kh)} KH | {zahl(n.fett)} F{n.ballaststoffe !== undefined ? ` | ${zahl(n.ballaststoffe)} B` : ''}
    </span>
  );
}

export default function RezeptAnsicht({ rezeptId, onClose, laden, startKcal = null }) {
  const [kcal, setKcal] = useState(startKcal);
  const [eingabe, setEingabe] = useState(startKcal ? String(startKcal) : '');
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);

  useEffect(() => { setKcal(startKcal); setEingabe(startKcal ? String(startKcal) : ''); setD(null); }, [rezeptId, startKcal]);
  useEffect(() => {
    if (!rezeptId) return undefined;
    let aktuell = true;
    setLaedt(true);
    setFehler('');
    laden(rezeptId, kcal).then((r) => { if (aktuell) setD(r.data); })
      .catch((e) => { if (aktuell) setFehler(e?.response?.data?.detail || 'Rezept konnte nicht geladen werden'); })
      .finally(() => { if (aktuell) setLaedt(false); });
    return () => { aktuell = false; };
  }, [rezeptId, kcal, laden]);

  const uebernehmen = (e) => {
    e.preventDefault();
    const w = Number(String(eingabe).replace(',', '.'));
    if (w >= 50 && w <= 3000) setKcal(Math.round(w));
  };

  return (
    <Dialog open={!!rezeptId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
        {!d && !fehler ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" aria-label="Lädt" /></div>
        ) : fehler && !d ? (
          <p className="p-6 text-sm text-destructive" role="alert">{fehler}</p>
        ) : (
          <>
            <div className="relative aspect-[16/9] bg-muted">
              {d.bild_url ? <img src={bildSrc(d.bild_url)} alt="" className="absolute inset-0 h-full w-full object-cover" />
                : <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><ChefHat className="w-12 h-12 opacity-40" aria-hidden="true" /></div>}
              {d.symbolbild && <span className="absolute right-2 bottom-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">Symbolbild</span>}
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              <DialogHeader className="text-left space-y-1.5">
                <div className="flex flex-wrap gap-1">
                  {Object.keys(MAHLZEIT).filter((m) => d.mahlzeiten.includes(m)).map((m) => <Badge key={m} variant="secondary" className="font-normal">{MAHLZEIT[m]}</Badge>)}
                </div>
                <DialogTitle className="text-xl leading-snug">{d.name}</DialogTitle>
                <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {d.zubereitungszeit_min != null || d.kochzeit_min != null
                    ? <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />Vorb. {d.zubereitungszeit_min ?? 0} Min. · Kochen {d.kochzeit_min ?? 0} Min.</span>
                    : d.minuten ? <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{d.minuten} Min.</span> : null}
                  {d.grund_portionen && d.grund_portionen !== 1 ? <span>{zahl(d.grund_portionen, 1)} Portionen</span> : null}
                  {d.schwierigkeit && <span>{SCHWIERIGKEIT[d.schwierigkeit]}</span>}
                  {d.budget && <span>Budget: {BUDGET[d.budget]}</span>}
                  {d.preis && <Preis p={d.preis} danach=" für diese Portion" />}
                </DialogDescription>
              </DialogHeader>

              <section aria-labelledby="portion-titel" className="rounded-lg border p-3 space-y-2">
                <h3 id="portion-titel" className="text-sm font-semibold">Portion für</h3>
                <div className="flex flex-wrap gap-1.5">
                  <Button type="button" size="sm" variant={kcal === null ? 'default' : 'outline'} onClick={() => { setKcal(null); setEingabe(''); }}>
                    Grundportion ({zahl(d.grund_kcal)} kcal)
                  </Button>
                  {VORGABEN.map((v) => (
                    <Button key={v} type="button" size="sm" variant={kcal === v ? 'default' : 'outline'} onClick={() => { setKcal(v); setEingabe(String(v)); }}>{v} kcal</Button>
                  ))}
                </div>
                <form onSubmit={uebernehmen} className="flex items-center gap-2">
                  <label htmlFor="portion-kcal" className="text-sm text-muted-foreground shrink-0">eigenes Ziel</label>
                  <Input id="portion-kcal" inputMode="numeric" className="w-28" value={eingabe} onChange={(e) => setEingabe(e.target.value)} placeholder="z. B. 650" />
                  <span className="text-sm text-muted-foreground">kcal</span>
                  <Button type="submit" size="sm" variant="secondary">Anpassen</Button>
                  {laedt && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Rechnet" />}
                </form>
                {!d.im_planbereich && (
                  <p className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                    Das ist das {zahl(d.faktor, 1)}-Fache der Grundportion. Im Plan wird ein Rezept nur zwischen halber und doppelter Menge eingesetzt.</p>
                )}
              </section>

              <section aria-labelledby="zutaten-titel" className="space-y-2">
                <h3 id="zutaten-titel" className="text-base font-semibold">Zutaten</h3>
                <ul className="divide-y rounded-lg border">
                  {d.zutaten.map((z) => (
                    <li key={z.lebensmittel_id} className="flex items-start justify-between gap-3 px-3 py-2">
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm break-words">{z.name}{z.quelle === 'openfoodfacts' && <span className="text-muted-foreground"> (OFF)</span>}
                          {z.alternativen?.length > 0 && <span className="text-muted-foreground"> · oder {z.alternativen.map((a) => a.name).join(', ')}</span>}
                          {z.gesperrt && <Badge variant="outline" className="ml-1.5 font-normal text-[10px]" title="Feste Menge — skaliert nicht mit dem Kalorienziel">fest</Badge>}
                        </p>
                        <NaehrChip n={z.naehrwerte} />
                      </div>
                      <span className="text-sm font-medium tabular-nums shrink-0">{z.einheit ? `${zahl(z.menge, 2)} ${z.einheit} · ` : ''}{zahl(z.gramm)} g</span>
                    </li>
                  ))}
                  {d.gewuerze.length > 0 && (
                    <li className="px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Nach Belieben: </span>{d.gewuerze.join(', ')}
                    </li>
                  )}
                </ul>
              </section>

              {d.schritte.length > 0 && (
                <section aria-labelledby="zubereitung-titel" className="space-y-2">
                  <h3 id="zubereitung-titel" className="text-base font-semibold">Zubereitung</h3>
                  <ol className="space-y-2 list-decimal pl-5 text-sm leading-relaxed">
                    {d.schritte.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </section>
              )}

              <section aria-labelledby="naehrwerte-titel" className="space-y-2">
                <h3 id="naehrwerte-titel" className="text-base font-semibold">Nährwerte</h3>
                <dl className="rounded-lg border divide-y text-sm">
                  {[['Kalorien', `${zahl(d.naehrwerte.kcal)} kcal`], ['Eiweiß', `${zahl(d.naehrwerte.eiweiss)} g`],
                    ['Kohlenhydrate', `${zahl(d.naehrwerte.kh)} g`], ['Fett', `${zahl(d.naehrwerte.fett)} g`],
                    ['Ballaststoffe', `${d.ballaststoffe.vollstaendig ? '' : 'mind. '}${zahl(d.ballaststoffe.gramm)} g`]].map(([k, v]) => (
                    <div key={k} className="flex justify-between px-3 py-1.5"><dt>{k}</dt><dd className="tabular-nums font-medium">{v}</dd></div>
                  ))}
                </dl>
                {d.datenquellen?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">Nährwerte: {d.datenquellen.map((q) => `${q.name} (${q.lizenz})`).join(' · ')}</p>
                )}
              </section>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
